import React, { useState } from 'react';
import {
    Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCardNumber, formatExpiry, detectCardBrand } from '../utils/formatters';
import BillingAddressForm from './BillingAddressForm';

const BRAND_COLORS = {
    visa: 'text-blue-600',
    mastercard: 'text-red-500',
    amex: 'text-green-600',
    discover: 'text-orange-500',
    unknown: 'text-slate-400',
};

const BRAND_LABELS = {
    visa: 'VISA',
    mastercard: '●● Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    unknown: '',
};

/**
 * Card payment form using react-hook-form + shadcn Form primitives.
 * Handles real-time formatting, CVV toggle, save-card checkbox, and billing address.
 *
 * @param {{ form: import('react-hook-form').UseFormReturn, onSubmit: Function, isProcessing: boolean }} props
 */
export function CardPaymentForm({ form, onSubmit, isProcessing }) {
    const [showCvv, setShowCvv] = useState(false);
    const [showBilling, setShowBilling] = useState(false);

    const cardNumberValue = form.watch('cardNumber') || '';
    const brand = detectCardBrand(cardNumberValue);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                noValidate
                className="space-y-5"
                data-testid="card-payment-form"
                aria-label="Card payment form"
            >
                {/* Cardholder Name */}
                <FormField
                    control={form.control}
                    name="cardholderName"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel htmlFor="cardholderName">Cardholder Name</FormLabel>
                            <FormControl>
                                <Input
                                    id="cardholderName"
                                    placeholder="Name on card"
                                    autoComplete="cc-name"
                                    data-testid="input-cardholder-name"
                                    aria-required="true"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                {/* Card Number */}
                <FormField
                    control={form.control}
                    name="cardNumber"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel htmlFor="cardNumber">Card Number</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        id="cardNumber"
                                        placeholder="1234 5678 9012 3456"
                                        autoComplete="cc-number"
                                        inputMode="numeric"
                                        maxLength={19}
                                        data-testid="input-card-number"
                                        aria-required="true"
                                        aria-describedby="card-brand-label"
                                        className="pr-20"
                                        value={field.value ? formatCardNumber(field.value) : ''}
                                        onChange={(e) => {
                                            const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
                                            field.onChange(raw);
                                        }}
                                        onBlur={field.onBlur}
                                    />
                                    {brand !== 'unknown' && (
                                        <span
                                            id="card-brand-label"
                                            className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold', BRAND_COLORS[brand])}
                                        >
                                            {BRAND_LABELS[brand]}
                                        </span>
                                    )}
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                {/* Expiry + CVV */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="expiryDate"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel htmlFor="expiryDate">Expiry Date</FormLabel>
                                <FormControl>
                                    <Input
                                        id="expiryDate"
                                        placeholder="MM/YY"
                                        autoComplete="cc-exp"
                                        inputMode="numeric"
                                        maxLength={5}
                                        data-testid="input-expiry"
                                        aria-required="true"
                                        value={field.value || ''}
                                        onChange={(e) => {
                                            field.onChange(formatExpiry(e.target.value));
                                        }}
                                        onBlur={field.onBlur}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="cvv"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel htmlFor="cvv">
                                    <span className="flex items-center gap-1">
                                        <Lock className="w-3 h-3" aria-hidden="true" />
                                        CVV
                                    </span>
                                </FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input
                                            id="cvv"
                                            type={showCvv ? 'text' : 'password'}
                                            placeholder="•••"
                                            autoComplete="cc-csc"
                                            inputMode="numeric"
                                            maxLength={4}
                                            data-testid="input-cvv"
                                            aria-required="true"
                                            className="pr-10"
                                            {...field}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCvv((v) => !v)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                            aria-label={showCvv ? 'Hide CVV' : 'Show CVV'}
                                        >
                                            {showCvv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                {/* Save Card */}
                <FormField
                    control={form.control}
                    name="saveCard"
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                            <FormControl>
                                <Checkbox
                                    id="saveCard"
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    data-testid="checkbox-save-card"
                                />
                            </FormControl>
                            <Label htmlFor="saveCard" className="text-sm text-slate-600 cursor-pointer">
                                Save this card for future payments
                            </Label>
                        </FormItem>
                    )}
                />

                {/* Billing Address Toggle */}
                <div>
                    <button
                        type="button"
                        onClick={() => setShowBilling((v) => !v)}
                        className="text-sm text-[#F87217] hover:text-[#C65B12] font-medium transition-colors"
                        data-testid="toggle-billing"
                        aria-expanded={showBilling}
                        aria-controls="billing-address-section"
                    >
                        {showBilling ? '− Hide' : '+ Add'} Billing Address (optional)
                    </button>

                    {showBilling && (
                        <div id="billing-address-section" className="mt-4">
                            <BillingAddressForm form={form} />
                        </div>
                    )}
                </div>

                {/* Security Notice */}
                <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Lock className="w-3 h-3" aria-hidden="true" />
                    Your payment info is encrypted and secure. We never store your CVV.
                </p>

                {/* Submit */}
                <Button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm btn-primary-glow font-semibold py-6"
                    data-testid="submit-card-payment"
                >
                    {isProcessing ? (
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Processing...
                        </span>
                    ) : (
                        'Pay Now'
                    )}
                </Button>
            </form>
        </Form>
    );
}

export default CardPaymentForm;
